import { useState, useEffect, useMemo } from 'react';
import { getCalendarEvents } from '../lib/api';
import { formatApiError } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, ClipboardText, CalendarCheck, Cake } from '@phosphor-icons/react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const eventColors = {
  task: { bg: 'bg-blue-50 border-blue-200', dot: 'bg-blue-500', text: 'text-blue-700' },
  follow_up: { bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700' },
  lead_follow_up: { bg: 'bg-orange-50 border-orange-200', dot: 'bg-orange-500', text: 'text-orange-700' },
  birthday: { bg: 'bg-pink-50 border-pink-200', dot: 'bg-pink-500', text: 'text-pink-700' }
};

const eventIcons = {
  task: ClipboardText,
  follow_up: CalendarCheck,
  lead_follow_up: CalendarCheck,
  birthday: Cake
};

export default function CalendarPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await getCalendarEvents({ month, year });
        setEvents(res.data);
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [month, year]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDate(null);
  };

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [month, year]);

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const day = parseInt(ev.date?.slice(8, 10), 10);
      if (!map[day]) map[day] = [];
      map[day].push(ev);
    }
    return map;
  }, [events]);

  const todayDay = now.getMonth() + 1 === month && now.getFullYear() === year ? now.getDate() : null;

  const selectedEvents = selectedDate ? (eventsByDate[selectedDate] || []) : [];

  // Summary counts
  const taskCount = events.filter(e => e.type === 'task').length;
  const fuCount = events.filter(e => e.type === 'follow_up' || e.type === 'lead_follow_up').length;
  const bdayCount = events.filter(e => e.type === 'birthday').length;

  return (
    <div className="p-6 space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Calendar</h1>
          <p className="text-sm text-zinc-500 mt-1">Tasks, follow-ups & birthdays at a glance</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> Tasks ({taskCount})</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span> Follow-ups ({fuCount})</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-pink-500"></span> Birthdays ({bdayCount})</span>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Calendar Grid */}
        <div className="flex-1">
          <Card className="border-zinc-200 rounded-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="cal-prev"><CaretLeft size={18} /></Button>
              <h2 className="text-lg font-semibold" data-testid="cal-month-year">{MONTHS[month - 1]} {year}</h2>
              <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="cal-next"><CaretRight size={18} /></Button>
            </div>
            <CardContent className="p-4">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20" />)}
                </div>
              ) : (
                <div className="grid grid-cols-7">
                  {/* Day headers */}
                  {DAYS.map(d => (
                    <div key={d} className="text-center text-[10px] uppercase tracking-wider font-semibold text-zinc-400 py-2 border-b border-zinc-100">{d}</div>
                  ))}
                  {/* Day cells */}
                  {calendarDays.map((day, idx) => {
                    const dayEvents = day ? (eventsByDate[day] || []) : [];
                    const isToday = day === todayDay;
                    const isSelected = day === selectedDate;
                    return (
                      <div
                        key={day ? `day-${day}` : `empty-${idx}`}
                        className={`min-h-[90px] p-1.5 border-b border-r border-zinc-100 cursor-pointer transition-colors ${
                          isSelected ? 'bg-[#002FA7]/5' : 'hover:bg-zinc-50'
                        } ${!day ? 'bg-zinc-50/50' : ''}`}
                        onClick={() => day && setSelectedDate(day === selectedDate ? null : day)}
                        data-testid={day ? `cal-day-${day}` : undefined}
                      >
                        {day && (
                          <>
                            <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                              isToday ? 'bg-[#002FA7] text-white' : ''
                            }`}>{day}</div>
                            <div className="space-y-0.5">
                              {dayEvents.slice(0, 3).map((ev, i) => {
                                const colors = eventColors[ev.type] || eventColors.task;
                                return (
                                  <div key={ev.id || `${ev.type}-${i}`} className={`text-[10px] px-1.5 py-0.5 rounded border truncate ${colors.bg} ${colors.text}`}>
                                    {ev.title}
                                  </div>
                                );
                              })}
                              {dayEvents.length > 3 && (
                                <div className="text-[10px] text-zinc-400 pl-1">+{dayEvents.length - 3} more</div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Event Detail Panel */}
        <div className="w-80 shrink-0">
          <Card className="border-zinc-200 rounded-sm sticky top-6">
            <div className="px-4 py-3 border-b border-zinc-100">
              <h3 className="font-semibold text-sm">
                {selectedDate
                  ? `${MONTHS[month - 1]} ${selectedDate}, ${year}`
                  : 'Select a day'}
              </h3>
              {selectedDate && (
                <p className="text-xs text-zinc-500 mt-0.5">{selectedEvents.length} event{selectedEvents.length !== 1 ? 's' : ''}</p>
              )}
            </div>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto" data-testid="cal-event-panel">
              {!selectedDate ? (
                <div className="p-6 text-center text-sm text-zinc-400">Click on a day to view events</div>
              ) : selectedEvents.length === 0 ? (
                <div className="p-6 text-center text-sm text-zinc-400">No events on this day</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {selectedEvents.map(ev => {
                    const colors = eventColors[ev.type] || eventColors.task;
                    const Icon = eventIcons[ev.type] || ClipboardText;
                    return (
                      <div key={`${ev.type}-${ev.id}`} className="p-3 flex gap-3" data-testid={`cal-event-${ev.id}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${colors.bg}`}>
                          <Icon size={14} className={colors.text} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{ev.title}</p>
                          {ev.client_name && <p className="text-xs text-zinc-500">{ev.client_name}</p>}
                          <div className="flex items-center gap-2 mt-1">
                            {ev.time && <span className="text-xs text-zinc-500">{ev.time}</span>}
                            {ev.status && (
                              <Badge className="text-[10px]" variant="secondary">{ev.status}</Badge>
                            )}
                            {ev.priority && (
                              <Badge className="text-[10px]" variant="outline">{ev.priority}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
